import a from "indefinite";
import { useState } from "react";
import {
  ArtifactType,
  CommonGoalArtifactFragmentFragment,
  GoalScope,
  GoalState,
  ProfileCareerTabCompetencyFragment,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import ArtifactCreationDialog from "@apps/artifact-creation-dialog/artifact-creation-dialog";
import Artifact from "@apps/artifact/artifact";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import GoalIcon from "@components/goal-icon/goal-icon";
import AppPopover from "@components/popover/app-popover";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon-helpers";

import getCareerRoleRelatedDataQuery from "../graphql/get-career-role-related-data-query";

const CompetencyGoals = ({
  competency,
  profile,
  roleId,
  roleTitle,
  goals,
}: {
  competency: ProfileCareerTabCompetencyFragment;
  profile: BasicUser;
  roleId: number;
  roleTitle: string;
  goals: CommonGoalArtifactFragmentFragment[];
}) => {
  const label = useLabel();
  const [showAddGoalModal, setShowAddGoalModal] = useState(false);

  const organization = currentOrganizationVar();
  if (!organization.featureFlags.goals) {
    return null;
  }

  const tooltipText =
    goals.length === 0
      ? `Add ${a(label("development"))} ${label("goal")}`
      : `${label("development", {
          capitalize: true,
        })} ${label("goal", {
          pluralize: true,
        })} connected to this ${label("competency")}.`;

  return (
    <div className="flex justify-between gap-2">
      {showAddGoalModal && (
        <ArtifactCreationDialog
          hideTypeSelector
          openSidebarAfterCreation
          formOptions={{
            artifactType: ArtifactType.Goal,
            scope: GoalScope.Development,
            owners: [profile],
            competencyIds: [{ competencyId: competency.id, roleId }],
          }}
          customFields={[
            {
              label: label("competency", { titleCase: true }),
              renderContent: (form) =>
                form.scope === GoalScope.Development ? (
                  <div className="flex">
                    <div className="py-0.5 px-1.5 bg-gray-100 rounded">
                      {competency.title} ({roleTitle})
                    </div>
                  </div>
                ) : null,
            },
          ]}
          onClose={() => setShowAddGoalModal(false)}
          refetchQueries={[getCareerRoleRelatedDataQuery]}
        />
      )}
      {goals.length === 0 ? (
        <Button
          size={ButtonSize.medium}
          onClick={() => setShowAddGoalModal(true)}
          tooltip={tooltipText}
          icon={TopicflowIconMap.plus}
        />
      ) : (
        <AppPopover
          content={
            <div className="flex flex-col gap-4 px-5 py-4 text-sm w-96">
              {goals.map((goal) => (
                <div key={goal.id} className="truncate">
                  <Artifact
                    artifact={goal}
                    size={5}
                    hideActionDropdown
                    showTitleOnlyOnOneLine
                  />
                </div>
              ))}
              <div className="-mx-3">
                <Button
                  theme={buttonTheme.text}
                  onClick={() => setShowAddGoalModal(true)}
                  fullWidth
                >
                  <TopicflowIcon type="plus" className="h-5 w-5" />
                  Create {label("development", { titleCase: true })}{" "}
                  {label("goal", { titleCase: true })}
                </Button>
              </div>
            </div>
          }
        >
          <AppPopover.Button
            as={Button}
            size={ButtonSize.medium}
            disabled={goals.length === 0}
            tooltip={tooltipText}
          >
            <GoalIcon
              size="4"
              state={GoalState.Open}
              scope={GoalScope.Development}
              hideTooltip
            />
            {goals.length}
          </AppPopover.Button>
        </AppPopover>
      )}
    </div>
  );
};

export default CompetencyGoals;
